/**
 * Memory 预览组件
 * 在 Memory 开关下方显示，展示最近 3 条记忆摘要（按时间倒序）
 *
 * Note: user_id 不再需要从前端传递，后端会从 token 中获取当前用户邮箱
 */

import { Box, Group, Text, ActionIcon, Skeleton, Stack } from '@mantine/core';
import { IconChevronRight, IconTrash, IconBrain } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { getMemories, deleteMemory, MemoryItem } from '@mygpt/app/apis/services/memory';
import { MemoryManageDrawer } from './MemoryManageDrawer';

interface MemoryPreviewProps {
  botId: string;
  enabled: boolean;
}

const MAX_PREVIEW_COUNT = 3;
const MAX_CONTENT_LENGTH = 60;

const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

const formatRelativeTime = (dateStr?: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return '刚刚';
  if (diffMins < 60) return `${diffMins}分钟前`;
  if (diffHours < 24) return `${diffHours}小时前`;
  if (diffDays < 7) return `${diffDays}天前`;
  return date.toLocaleDateString();
};

// 按时间倒序排序（最新的在前）
const sortMemoriesByTimeDesc = (memories: MemoryItem[]): MemoryItem[] => {
  return [...memories].sort((a, b) => {
    const timeA = new Date(a.updated_at || a.created_at || 0).getTime();
    const timeB = new Date(b.updated_at || b.created_at || 0).getTime();
    return timeB - timeA;
  });
};

export const MemoryPreview = ({ botId, enabled }: MemoryPreviewProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [drawerOpened, setDrawerOpened] = useState(false);

  // 获取记忆列表（user_id 从后端 token 获取）
  const { data, isLoading, error } = useQuery({
    queryKey: ['memories', botId],
    queryFn: () => getMemories(botId),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });

  // 删除单条记忆
  const deleteMutation = useMutation({
    mutationFn: (memoryId: string) => deleteMemory(memoryId, botId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memories', botId] });
    },
  });

  // 按时间倒序排序
  const sortedMemories = useMemo(() => {
    const memories = data?.memories || [];
    return sortMemoriesByTimeDesc(memories);
  }, [data?.memories]);

  const previewMemories = sortedMemories.slice(0, MAX_PREVIEW_COUNT);
  const totalCount = data?.total || 0;

  // 如果 Memory 未启用，不显示任何内容
  if (!enabled) {
    return null;
  }

  const handleManageClick = () => {
    setDrawerOpened(true);
  };

  const handleDeleteMemory = (memoryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteMutation.mutate(memoryId);
  };

  return (
    <>
      <Box className="flex-1 bg-gray-50 rounded-lg p-4 mt-2">
        <Group justify="space-between" mb={sortedMemories.length > 0 ? 'xs' : 0}>
          <Group gap="xs">
            <IconBrain size={16} color="gray" />
            <Text size="sm" c="gray.7">
              {t('settings.memory.preview_title', '记忆预览')}
            </Text>
            {totalCount > 0 && (
              <Text size="xs" c="gray.5">
                ({totalCount})
              </Text>
            )}
          </Group>
          <Group gap="xs">
            <Text size="sm" c="violet" className="cursor-pointer hover:underline" onClick={handleManageClick}>
              {t('settings.memory.manage', '管理记忆')}
            </Text>
            <IconChevronRight size={14} color="var(--mantine-color-violet-6)" />
          </Group>
        </Group>

        {isLoading ? (
          <Stack gap="xs">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={24} radius="sm" />
            ))}
          </Stack>
        ) : error ? (
          <Text size="xs" c="red">
            {t('settings.memory.load_error', '加载失败')}
          </Text>
        ) : sortedMemories.length === 0 ? (
          <Text size="xs" c="gray.5">
            {t('settings.memory.empty', '尚无记忆内容')}
          </Text>
        ) : (
          <Stack gap="xs">
            {previewMemories.map((memory) => (
              <Group key={memory.id} justify="space-between" className="bg-white rounded p-2">
                <Text size="xs" className="flex-1" title={memory.content}>
                  {truncateText(memory.content, MAX_CONTENT_LENGTH)}
                </Text>
                <Group gap="xs">
                  <Text size="xs" c="gray.5">
                    {formatRelativeTime(memory.updated_at || memory.created_at)}
                  </Text>
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={(e) => handleDeleteMemory(memory.id, e)}
                    loading={deleteMutation.isPending}
                  >
                    <IconTrash size={12} />
                  </ActionIcon>
                </Group>
              </Group>
            ))}
            {totalCount > MAX_PREVIEW_COUNT && (
              <Text size="xs" c="gray.5" ta="center">
                {t('settings.memory.more_count', {
                  count: totalCount - MAX_PREVIEW_COUNT,
                  defaultValue: `还有 ${totalCount - MAX_PREVIEW_COUNT} 条记忆`,
                })}
              </Text>
            )}
          </Stack>
        )}
      </Box>

      <MemoryManageDrawer opened={drawerOpened} onClose={() => setDrawerOpened(false)} botId={botId} />
    </>
  );
};
